import React from 'react';
import { isCompatible } from '../../../../../src/webparts/edinshallAudit/services/installer/versioning/SchemaVersionStamp';

export type InstanceStatus = 'active' | 'recent' | 'stale' | 'superseded' | 'deregistered';
export type MaintenanceAction = 'install' | 'repair' | 'upgrade';
export type MaintenanceStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'cancelled';

export interface InstanceCardModel {
  instanceId: string;
  siteUrl: string;
  pageUrl: string;
  webPartVersion: string;
  schemaVersion: string | null;
  requiredSchemaVersion: string;
  mode: string;
  lastSeenAt: string;
  daysSinceLastSeen: number;
  status: InstanceStatus;
  isOutdated: boolean;
  /** Present when a newer web part instance on the same site has reported since this one was last seen. */
  supersededBy?: { instanceId: string; registeredAt: string } | null;
  listVisibility?: {
    visibilityState: 'hidden' | 'revealed';
    lastAppliedAt: string | null;
    lastError: string | null;
  };
  attention?: {
    runtimeGate: string;
    installerState: string | null;
    reason: string | null;
    notificationSummary: string | null;
  } | null;
  maintenance?: {
    operationId?: string;
    requestedAction: MaintenanceAction;
    status: MaintenanceStatus;
    stage: string;
    statusMessage: string | null;
    updatedAt?: string;
  } | null;
  mailDelivery?: {
    uncertainCount: number;
    heldCount: number;
    lastUncertainAt: string | null;
  };
}

export interface InstanceAdminInspection {
  fetchedAt: string;
  cached: boolean;
  memberCount: number;
  members: Array<{ title: string; loginName: string; email: string }>;
}

interface InstanceCardProps {
  instance: InstanceCardModel;
  // operational state
  siteGranted: boolean;
  canInspectAdmins: boolean;
  isConnected: boolean;
  interactiveInspectorActions: boolean;
  inspectorAppClientId: string;
  // per-action loading
  loading: {
    maintenance: boolean;
    dismiss?: boolean;
    deregister?: boolean;
    inspect: boolean;
    listVisibility: boolean;
    grant: boolean;
    mailResend: boolean;
  };
  // per-action error
  errors: {
    maintenance?: string;
    dismiss?: string;
    deregister?: string;
    inspect?: string;
    listVisibility?: string;
    grant?: string;
    mailResend?: string;
  };
  adminInspection?: InstanceAdminInspection;
  // handlers
  onRequestMaintenance: (action: 'repair') => void;
  /** Hides a finished (succeeded/failed/cancelled) maintenance result from the card. */
  onDismissMaintenance?: (operationId: string) => void;
  /** Cancels a pending or running maintenance operation that is not making progress. */
  onCancelMaintenance?: (operationId: string) => void;
  /** Marks this installation deregistered (site deleted or web part removed). Self-heals on a heartbeat from the same registered site; a site rename needs explicit binding recovery. */
  onDeregister?: () => void;
  onInspectAdmins: () => void;
  onRevealLists: () => void;
  onHideLists: () => void;
  onGrantSite: () => void;
  onRevokeSite: () => void;
  onResendHeldMail: () => void;
  // helpers
  formatRelative: (iso: string) => string;
  prettyHost: (url: string) => string;
  heartbeatHealth: 'fresh' | 'low' | 'stale';
  /** Extra action groups rendered inside the actions area (e.g. the zero-touch scheduling card, tracker #56). */
  extraActionGroups?: React.ReactNode;
}

const STATUS_LABEL: Record<InstanceStatus, string> = {
  active: 'Active',
  recent: 'Recent',
  stale: 'Stale',
  superseded: 'Superseded',
  deregistered: 'Deregistered'
};

const HEARTBEAT_LABEL: Record<'fresh' | 'low' | 'stale', string> = {
  fresh: 'Fresh',
  low: 'Low activity',
  stale: 'Stale heartbeat'
};

const HEARTBEAT_DETAIL: Record<'fresh' | 'low' | 'stale', string> = {
  fresh: 'Within the last 10 minutes',
  low: 'Within the last hour',
  stale: 'No heartbeat in over an hour'
};

const requiresSchemaUpdate = (instance: InstanceCardModel): boolean => {
  if (!instance.schemaVersion || !instance.requiredSchemaVersion) return false;
  const compatibility = isCompatible(instance.schemaVersion, instance.requiredSchemaVersion);
  return compatibility === 'minor-upgrade' || compatibility === 'major-upgrade';
};

/**
 * Per-instance card. The header carries identity and state; the status grid
 * gives the operator everything they need to answer "what's the state" without
 * scrolling; the notice band only renders when there's actionable attention
 * or a maintenance update; actions are grouped semantically so the operator
 * doesn't have to scan a flat row of generic buttons.
 */
export const InstanceCard: React.FC<InstanceCardProps> = ({
  instance,
  siteGranted,
  canInspectAdmins,
  isConnected,
  interactiveInspectorActions,
  inspectorAppClientId,
  loading,
  errors,
  adminInspection,
  onRequestMaintenance,
  onDismissMaintenance,
  onCancelMaintenance,
  onDeregister,
  onInspectAdmins,
  onRevealLists,
  onHideLists,
  onGrantSite,
  onRevokeSite,
  onResendHeldMail,
  formatRelative,
  prettyHost,
  heartbeatHealth,
  extraActionGroups
}) => {
  const needsAttention = Boolean(instance.attention) || instance.maintenance?.status === 'failed';
  const requestedAction = 'repair' as const;
  const needsFirstInstallation = instance.attention?.runtimeGate === 'RequiresInstall';
  const needsSchemaUpdate = requiresSchemaUpdate(instance);
  const listsRevealed = instance.listVisibility?.visibilityState === 'revealed';
  const isDeregistered = instance.status === 'deregistered';
  const isSuperseded = instance.status === 'superseded';
  const maintenanceRunning = instance.maintenance?.status === 'running';
  const maintenanceFinished =
    instance.maintenance?.status === 'succeeded' ||
    instance.maintenance?.status === 'failed' ||
    instance.maintenance?.status === 'cancelled';
  const canDismissMaintenance = Boolean(maintenanceFinished && instance.maintenance?.operationId && onDismissMaintenance);
  const maintenanceLive = instance.maintenance?.status === 'pending' || instance.maintenance?.status === 'running';
  const canCancelMaintenance = Boolean(maintenanceLive && instance.maintenance?.operationId && onCancelMaintenance);
  const inspectorSiteId = (() => {
    try {
      const url = new URL(instance.siteUrl);
      return `${url.hostname}:${url.pathname}`;
    } catch {
      return instance.siteUrl;
    }
  })();
  const inspectorGrantSnippet = [
    'Connect-MgGraph -Scopes "Sites.FullControl.All"',
    `$site = Get-MgSite -SiteId "${inspectorSiteId}"`,
    '$permission = New-MgSitePermission -SiteId $site.Id -BodyParameter @{',
    '  roles = @("read")',
    `  grantedToIdentities = @(@{ application = @{ id = "${inspectorAppClientId}"; displayName = "Edinshall Inspector" } })`,
    '}',
    'Write-Output "Keep this permission ID for later removal: $($permission.Id)"'
  ].join('\n');

  const cardClasses = ['installation-card'];
  if (needsAttention) cardClasses.push('needs-attention');
  if (isDeregistered) cardClasses.push('is-deregistered');
  if (isSuperseded) cardClasses.push('is-superseded');

  return (
    <article className={cardClasses.join(' ')}>
      <header className="installation-card-header">
        <div className="installation-card-host">{prettyHost(instance.siteUrl)}</div>
        <div className="installation-card-meta">
          <span className={`installation-state state-${instance.status}`}>
            <span className="dot" />
            {STATUS_LABEL[instance.status]}
          </span>
          <span
            className={instance.isOutdated ? 'pill warning' : 'pill success'}
            title={instance.isOutdated ? 'A newer web part version is available' : 'Web part is current'}
          >
            v{instance.webPartVersion || 'unknown'}
          </span>
        </div>
      </header>

      <div className="installation-page">
        <span className="label">Page</span>
        {instance.pageUrl || '(not reported)'}
      </div>

      <div className="installation-status-grid">
        <div className={`installation-stat ${heartbeatHealth === 'stale' ? 'stat-warning' : heartbeatHealth === 'fresh' ? 'stat-success' : ''}`}>
          <div className="stat-label">Heartbeat</div>
          <div className="stat-value-row">
            <span className="stat-value">{HEARTBEAT_LABEL[heartbeatHealth]}</span>
          </div>
          <div className="stat-detail">{HEARTBEAT_DETAIL[heartbeatHealth]} · {formatRelative(instance.lastSeenAt)}</div>
        </div>

        <div className="installation-stat">
          <div className="stat-label">Mode</div>
          <div className="stat-value">{instance.mode || 'local'}</div>
          <div className="stat-detail">Configuration model reported by the web part</div>
        </div>

        <div className={`installation-stat ${instance.schemaVersion === instance.requiredSchemaVersion ? 'stat-success' : 'stat-warning'}`}>
          <div className="stat-label">Verified site schema</div>
          <div className="stat-value">{instance.schemaVersion || 'Not yet reported'}</div>
          <div className="stat-detail">Required by this release: {instance.requiredSchemaVersion || 'unknown'}</div>
        </div>

        <div className={`installation-stat ${listsRevealed ? 'stat-warning' : ''}`}>
          <div className="stat-label">Lists visibility</div>
          <div className="stat-value">{listsRevealed ? 'Revealed to operators' : 'Hidden (default)'}</div>
          <div className="stat-detail">
            {instance.listVisibility?.lastAppliedAt
              ? `Last changed ${formatRelative(instance.listVisibility.lastAppliedAt)}`
              : 'Protected lists always stay hidden'}
          </div>
        </div>

        {instance.maintenance && (
          <div
            className={`installation-stat ${
              instance.maintenance.status === 'failed'
                ? 'stat-danger'
                : instance.maintenance.status === 'running'
                ? 'stat-warning'
                : instance.maintenance.status === 'succeeded'
                ? 'stat-success'
                : ''
            }`}
          >
            <div className="stat-label">Maintenance</div>
            <div className="stat-value">
              {instance.maintenance.requestedAction} · {instance.maintenance.status}
            </div>
            <div className="stat-detail">
              {instance.maintenance.stage}
              {instance.maintenance.statusMessage ? ` · ${instance.maintenance.statusMessage}` : ''}
            </div>
          </div>
        )}
        {Boolean(instance.mailDelivery?.uncertainCount) && (
          <div className={`installation-stat ${instance.mailDelivery?.heldCount ? 'stat-warning' : ''}`}>
            <div className="stat-label">E-mail delivery</div>
            <div className="stat-value">
              {instance.mailDelivery?.uncertainCount} uncertain
              {instance.mailDelivery?.heldCount ? ` · ${instance.mailDelivery.heldCount} held` : ' · automatic retry active'}
            </div>
            <div className="stat-detail">
              {instance.mailDelivery?.lastUncertainAt
                ? `Last uncertainty ${formatRelative(instance.mailDelivery.lastUncertainAt)}`
                : 'Graph did not return a definitive acceptance response'}
            </div>
          </div>
        )}
      </div>

      <ol className="installation-evidence-journey" aria-label="Installation evidence journey">
        <li>
          <strong>1. Loaded package</strong> — heartbeat reports v{instance.webPartVersion || 'unknown'}.
        </li>
        <li>
          <strong>2. Site maintenance</strong> — {instance.maintenance
            ? `${instance.maintenance.requestedAction} is ${instance.maintenance.status} (${instance.maintenance.stage})`
            : 'no Portal maintenance operation is recorded'}.
        </li>
        <li>
          <strong>3. Site heartbeat</strong> — {HEARTBEAT_LABEL[heartbeatHealth].toLowerCase()}, reporting verified schema {instance.schemaVersion || 'not yet recorded'}.
        </li>
        <li>
          <strong>4. Active attention</strong> — {instance.attention ? instance.attention.runtimeGate : 'none'}.
        </li>
      </ol>

      {instance.attention && (
        <div className="installation-notice notice-warning">
          <span className="icon" aria-hidden="true">⚠️</span>
          <div className="text">
            <strong>Site needs attention.</strong>{' '}
            {instance.attention.runtimeGate}
            {instance.attention.installerState ? ` · ${instance.attention.installerState}` : ''}
            {instance.attention.reason ? ` — ${instance.attention.reason}` : ''}
            {instance.attention.notificationSummary && (
              <>
                <br />
                {instance.attention.notificationSummary}
              </>
            )}
          </div>
        </div>
      )}

      {isSuperseded && (
        <div className="installation-notice notice-info">
          <span className="icon" aria-hidden="true">↻</span>
          <div className="text">
            <strong>Superseded.</strong>{' '}
            A newer web part instance on this site has reported since this one was last seen
            {instance.supersededBy?.registeredAt ? ` (registered ${formatRelative(instance.supersededBy.registeredAt)})` : ''}.
            That usually means the web part was removed and re-added, or the page was copied. If this page still exists, opening it will reactivate this entry.
          </div>
        </div>
      )}

      {instance.maintenance?.status === 'failed' && (
        <div className="installation-notice notice-danger">
          <span className="icon" aria-hidden="true">✕</span>
          <div className="text">
            <strong>Maintenance failed{instance.maintenance.updatedAt ? ` ${formatRelative(instance.maintenance.updatedAt)}` : ''}.</strong>{' '}
            {instance.maintenance.statusMessage || 'The platform reported the operation as failed. Review the error and request another run.'}
          </div>
        </div>
      )}

      {instance.isOutdated && (
        <div className="installation-notice notice-warning">
          <span className="icon" aria-hidden="true">&#8593;</span>
          <div className="text">
            <strong>A newer package is available.</strong>{' '}
            Site maintenance verifies and repairs SharePoint schema; it does not change or attest the loaded web-part package.
            Deploy an approved package from <a href="/portal/admin/spfx-releases">SPFx Releases</a>, then use the next site heartbeat as the version evidence.
          </div>
        </div>
      )}

      {!siteGranted && isConnected && interactiveInspectorActions && (
        <div className="installation-notice notice-info">
          <span className="icon" aria-hidden="true">🔑</span>
          <div className="text">
            <strong>Customer-admin site assignment not yet verified.</strong>{' '}
            A customer administrator must assign the Inspector app read access to this exact site, then verify it here. The ordinary Portal does not request tenant-wide site-control permission.
            {inspectorAppClientId ? <> App ID: <code>{inspectorAppClientId}</code>.</> : null}
          </div>
        </div>
      )}

      <div className="installation-actions">
        {Boolean(instance.mailDelivery?.heldCount) && (
          <div className="installation-action-group">
            <div className="group-label">Held e-mail</div>
            <div className="group-actions">
              <button className="btn secondary" type="button" onClick={onResendHeldMail} disabled={loading.mailResend}>
                {loading.mailResend ? 'Queueing…' : `Queue ${instance.mailDelivery?.heldCount} held message${instance.mailDelivery?.heldCount === 1 ? '' : 's'} for resend`}
              </button>
            </div>
            {errors.mailResend && <div className="muted" style={{ color: 'var(--danger)', fontSize: 12 }}>{errors.mailResend}</div>}
          </div>
        )}
        <div className="installation-action-group">
          <div className="group-label">Maintenance</div>
          <div className="group-actions">
            {needsFirstInstallation ? (
              <a className="btn" href={instance.pageUrl || instance.siteUrl}>
                Open SharePoint to complete installation
              </a>
            ) : (
              <button
                className="btn"
                type="button"
                onClick={() => onRequestMaintenance(requestedAction)}
                disabled={loading.maintenance || maintenanceLive || isSuperseded || isDeregistered}
              >
                {loading.maintenance
                  ? 'Submitting…'
                  : needsSchemaUpdate
                  ? 'Update and verify site schema'
                  : 'Verify and repair site schema'}
              </button>
            )}
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            {needsFirstInstallation
              ? 'Fresh installation requires an explicit first Edinshall System Administrator selection in SharePoint.'
              : isSuperseded
              ? 'Request maintenance from the current instance of this site instead.'
              : 'Runs in the Portal against this site with your delegated SharePoint access: readiness, seed and final-integrity checks. Package deployment is managed separately in SPFx Releases.'}
          </div>
          {errors.maintenance && <div className="muted" style={{ color: 'var(--danger)', fontSize: 12 }}>{errors.maintenance}</div>}
          {canCancelMaintenance && (
            <div className="group-actions">
              <button
                className="btn secondary"
                type="button"
                onClick={() => onCancelMaintenance?.(instance.maintenance!.operationId!)}
                disabled={loading.dismiss}
              >
                {loading.dismiss ? 'Cancelling…' : 'Cancel this maintenance operation'}
              </button>
            </div>
          )}
          {canDismissMaintenance && (
            <div className="group-actions">
              <button
                className="btn secondary"
                type="button"
                onClick={() => onDismissMaintenance?.(instance.maintenance!.operationId!)}
                disabled={loading.dismiss}
              >
                {loading.dismiss ? 'Dismissing…' : 'Dismiss last maintenance result'}
              </button>
            </div>
          )}
          {errors.dismiss && <div className="muted" style={{ color: 'var(--danger)', fontSize: 12 }}>{errors.dismiss}</div>}
        </div>

        {onDeregister && !isDeregistered && (
          <div className="installation-action-group">
            <div className="group-label">Registration</div>
            <div className="group-actions">
              <button
                className="btn secondary"
                type="button"
                onClick={() => onDeregister()}
                disabled={loading.deregister}
              >
                {loading.deregister ? 'Deregistering…' : 'Deregister installation'}
              </button>
            </div>
            <div className="muted" style={{ fontSize: 12 }}>
              Use when the site was deleted or the web part removed. Nothing is deleted; the entry comes back on its own if the page ever reports again.
            </div>
            {errors.deregister && <div className="muted" style={{ color: 'var(--danger)', fontSize: 12 }}>{errors.deregister}</div>}
          </div>
        )}

        <div className="installation-action-group">
          <div className="group-label">Content visibility</div>
          <div className="group-actions">
            <button
              className="btn secondary"
              type="button"
              onClick={listsRevealed ? onHideLists : onRevealLists}
              disabled={loading.listVisibility || !canInspectAdmins}
              title={!canInspectAdmins ? 'Connect tenant and grant site access first' : undefined}
            >
              {loading.listVisibility
                ? listsRevealed ? 'Hiding lists…' : 'Revealing lists…'
                : listsRevealed ? 'Hide lists now' : 'Reveal lists'}
            </button>
          </div>
          {errors.listVisibility && <div className="muted" style={{ color: 'var(--danger)', fontSize: 12 }}>{errors.listVisibility}</div>}
          {instance.listVisibility?.lastError && (
            <div className="muted" style={{ color: 'var(--danger)', fontSize: 12 }}>
              Last list error: {instance.listVisibility.lastError}
            </div>
          )}
        </div>

        <div className="installation-action-group">
          <div className="group-label">Admins</div>
          <div className="group-actions">
            {!siteGranted && (
              <button
                className="btn secondary"
                type="button"
                onClick={onGrantSite}
                disabled={loading.grant || !isConnected || !interactiveInspectorActions}
              >
                {loading.grant ? 'Verifying…' : 'Verify customer-admin assignment'}
              </button>
            )}
            {siteGranted && (
              <button
                className="btn secondary"
                type="button"
                onClick={onRevokeSite}
                disabled={loading.grant}
                title="Stops Inspector use immediately; Microsoft 365 assignment removal remains a customer-admin step"
              >
                {loading.grant ? 'Revoking…' : 'Revoke Inspector in Edinshall'}
              </button>
            )}
            <button
              className="btn secondary"
              type="button"
              onClick={onInspectAdmins}
              disabled={loading.inspect || !canInspectAdmins}
            >
              {loading.inspect ? 'Inspecting…' : 'Inspect admins'}
            </button>
          </div>
          {errors.grant && <div className="muted" style={{ color: 'var(--danger)', fontSize: 12 }}>{errors.grant}</div>}
          {errors.inspect && <div className="muted" style={{ color: 'var(--danger)', fontSize: 12 }}>{errors.inspect}</div>}
          {!siteGranted && interactiveInspectorActions && inspectorAppClientId && (
            <details className="muted" style={{ fontSize: 12 }}>
              <summary>Customer-admin site assignment</summary>
              <p>Inspector is optional. A customer administrator grants this app read access to this audit site only, then Edinshall verifies it.</p>
              <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{inspectorGrantSnippet}</pre>
            </details>
          )}
        </div>

        {extraActionGroups}
      </div>

      {adminInspection && (
        <div className="installation-admins">
          <div className="installation-admins-header">
            <strong>System admins ({adminInspection.memberCount})</strong>
            <span className="muted">fetched {formatRelative(adminInspection.fetchedAt)}{adminInspection.cached ? ' (cached)' : ''}</span>
          </div>
          <div className="installation-admins-list">
            {adminInspection.members.map((member) => (
              <span key={member.loginName || member.email || member.title} className="installation-admin-chip" title={member.email || member.loginName}>
                👤 {member.title || member.email || member.loginName}
              </span>
            ))}
          </div>
        </div>
      )}
    </article>
  );
};

export default InstanceCard;
